// Tab 4: Istoric stoc — movements timeline.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowUpRight, ArrowDownRight, Settings2, Loader2, ShoppingBag, History,
  PackageCheck, RotateCcw, Sparkles,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import { listMovements } from "@/lib/inventory";
import { fmtDate } from "@/lib/format";

const TYPE_META = {
  order_consumed: { label: "Comandă", icon: ShoppingBag, color: "text-red-500" },
  order_restored: { label: "Comandă anulată", icon: RotateCcw, color: "text-blue-500" },
  return_restock: { label: "Retur", icon: RotateCcw, color: "text-emerald-500" },
  manual_in: { label: "Intrare marfă", icon: PackageCheck, color: "text-emerald-500" },
  manual_out: { label: "Ieșire marfă", icon: ArrowDownRight, color: "text-red-500" },
  manual_adjust: { label: "Ajustare manuală", icon: Settings2, color: "text-amber-500" },
  init: { label: "Snapshot inițial", icon: Sparkles, color: "text-purple-500" },
};


export default function MovementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filter !== "ALL") params.type = filter;
      const r = await listMovements(params);
      setItems(r.items || []);
    } catch { toast.error("Eroare"); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-3" data-testid="movements-tab">
      <div className="flex items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-[220px]" data-testid="movements-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Toate mișcările</SelectItem>
            {Object.entries(TYPE_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">{items.length} mișcări</div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <History className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <div className="text-sm">Niciun istoric încă.</div>
        </Card>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-semibold w-44">Data</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-semibold">Tip</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-semibold">SKU</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">Δ</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">Înainte</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">După</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-semibold">Notă</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-semibold">Operator</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const meta = TYPE_META[m.type] || { label: m.type, icon: Settings2, color: "text-muted-foreground" };
                const Icon = meta.icon;
                const positive = m.delta > 0;
                return (
                  <tr key={m.id} className="border-b border-border/40 last:border-0">
                    <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums whitespace-nowrap">{fmtDate(m.created_at)}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] ${meta.color}`}>
                        <Icon className="h-3 w-3 mr-1" /> {meta.label}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{m.sku || "—"}</td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${positive ? "text-emerald-500" : "text-red-500"}`}>
                      {positive ? <ArrowUpRight className="h-3 w-3 inline mr-0.5" /> : <ArrowDownRight className="h-3 w-3 inline mr-0.5" />}
                      {positive ? `+${m.delta}` : m.delta}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground tabular-nums">{m.qty_before}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{m.qty_after}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[220px] truncate">{m.notes || "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{m.actor_email?.split("@")[0] || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
